import { Dropdown, Modal, Col, Row, Space, Typography, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import Loader from "../controls/Loader";
import { Incident } from "../repository/data.default";
import { getIncidentsExport } from "../repository/data.repository";
import IncidentsView from "../components/incident/IncidentsView";
import IncidentFormDialog from "../components/incident/IncidentFormDialog";
import IncidentManageDialog from "../components/incident/IncidentManageDialog";
import { IncidentForm } from "../components/incident/IncidentFormDialog.default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function IncidentsPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });

  // Create / delete go through the lean form dialog; "Edit" opens the ticket Manage dialog.
  const [formMode, setFormMode] = useState<"add" | "delete">("add");
  const [formOpen, setFormOpen] = useState(false);
  const [formValues, setFormValues] = useState<IncidentForm>({} as any);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageRecord, setManageRecord] = useState<Incident | undefined>();

  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onIncidentAction(key: string, record?: Incident): void {
    if (key === "update") {
      setManageRecord(record);
      setManageOpen(true);
      return;
    }
    // "add" (no record) or "delete" (with record)
    setFormMode(key as "add" | "delete");
    setFormValues(
      record ? ({ uid: record.uid, title: record.title } as any) : ({} as any),
    );
    setFormOpen(true);
  }

  function onFinish(): void {
    setFormOpen(false);
    setManageOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading incidents export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getIncidentsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Incidents
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("Incidents", "create")}

            className={`${!permissions.can("Incidents", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Incidents", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onIncidentAction("add")}
          >
            Report Incident
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <IncidentsView
          trigger={triggerView}
          onAction={(key: string, record: any) => onIncidentAction(key, record)}
          onSearch={onSearch}
        />
      </Col>
      <IncidentFormDialog
        mode={formMode}
        open={formOpen}
        initialValues={formValues}
        onFinish={onFinish}
        onCancel={() => setFormOpen(false)}
      />
      <IncidentManageDialog
        open={manageOpen}
        record={manageRecord}
        onFinish={onFinish}
        // Closing also refreshes the grid so any status/assignee edits made inside are reflected.
        onCancel={onFinish}
      />
      {contextHolder}
    </Col>
  );
}

export default IncidentsPage;
